import {
  computed,
  inject,
  ref,
  watch,
  type ComputedRef,
} from 'vue'
import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/vue-query'

import { FalhaHttp } from '@next/infraestrutura/http'

import { criarQueryKeyCatalogo } from './catalogo.chave'
import { CHAVE_CATALOGOS_PERSISTIDOS } from './catalogo.injecao'
import { criarSnapshotCatalogo, compartilharSnapshotCatalogo } from './catalogo.sincronizacao'
import {
  DURACAO_CATALOGO_FRESCO_MS,
  dataAtualizacaoParaHidratacao,
} from './catalogo.politica'
import type {
  CatalogoDescriptor,
  CatalogosPersistidos,
  ContextoDeCatalogo,
} from './catalogo.tipos'

export type ConfiguracaoCatalogoQuery<T> = Readonly<{
  catalogo: CatalogoDescriptor<T>
  contexto: ComputedRef<ContextoDeCatalogo | null>
  consultar: (sinal: AbortSignal) => Promise<readonly T[]>
  habilitado?: ComputedRef<boolean>
}>

export type EstadoCatalogoQuery<T> = Readonly<{
  itens: ComputedRef<readonly T[]>
  erro: ComputedRef<Error | null>
  carregando: ComputedRef<boolean>
  atualizando: ComputedRef<boolean>
  atualizar: () => Promise<void>
}>

const hidratacoes = new WeakMap<QueryClient, Map<string, Promise<void>>>()

export const hidratarCatalogoPersistido = async <T>(
  queryClient: QueryClient,
  catalogosPersistidos: CatalogosPersistidos,
  catalogo: CatalogoDescriptor<T>,
  contexto: ContextoDeCatalogo,
  agora = Date.now(),
): Promise<void> => {
  const chave = criarQueryKeyCatalogo(catalogo.nome, contexto)
  if (queryClient.getQueryData(chave) !== undefined) return
  const identificador = JSON.stringify([chave, catalogo.versaoProjecao])
  const pendentes = hidratacoes.get(queryClient) ?? new Map<string, Promise<void>>()
  hidratacoes.set(queryClient, pendentes)
  const pendente = pendentes.get(identificador)
  if (pendente) return pendente
  const leitura = (async () => {
    const resultado = await catalogosPersistidos.ler(catalogo, contexto)
    if (resultado.estado !== 'fresco' && resultado.estado !== 'desatualizado') return
    if (queryClient.getQueryData(chave) !== undefined) return
    queryClient.setQueryData(chave, criarSnapshotCatalogo(resultado.itens, resultado.sincronizadoEm), {
      updatedAt: dataAtualizacaoParaHidratacao(resultado.sincronizadoEm, resultado.estado, agora),
    })
  })()
  pendentes.set(identificador, leitura)
  try {
    await leitura
  } finally {
    if (pendentes.get(identificador) === leitura) pendentes.delete(identificador)
  }
}

export const criarOpcoesConsultaCatalogo = <T>(
  catalogosPersistidos: CatalogosPersistidos,
  catalogo: CatalogoDescriptor<T>,
  contexto: ContextoDeCatalogo,
  consultar: (sinal: AbortSignal) => Promise<readonly T[]>,
) => ({
  queryKey: criarQueryKeyCatalogo(catalogo.nome, contexto),
  staleTime: DURACAO_CATALOGO_FRESCO_MS,
  structuralSharing: compartilharSnapshotCatalogo,
  queryFn: async ({ signal }: { signal: AbortSignal }) => {
    const itens = await consultar(signal)
    signal.throwIfAborted()
    const sincronizadoEm = Date.now()
    const snapshot = criarSnapshotCatalogo(itens, sincronizadoEm)
    await catalogosPersistidos.salvar(catalogo, contexto, snapshot, sincronizadoEm)
    signal.throwIfAborted()
    return snapshot
  },
})

export const carregarCatalogo = async <T>(
  queryClient: QueryClient,
  catalogosPersistidos: CatalogosPersistidos,
  catalogo: CatalogoDescriptor<T>,
  contexto: ContextoDeCatalogo,
  consultar: (sinal: AbortSignal) => Promise<readonly T[]>,
  aindaNecessario: () => boolean = () => true,
): Promise<readonly T[]> => {
  await hidratarCatalogoPersistido(queryClient, catalogosPersistidos, catalogo, contexto)
  if (!aindaNecessario()) return []
  return queryClient.fetchQuery(criarOpcoesConsultaCatalogo(
    catalogosPersistidos, catalogo, contexto, consultar,
  ))
}

export const invalidarCatalogo = async <T>(
  queryClient: QueryClient,
  catalogosPersistidos: CatalogosPersistidos,
  catalogo: CatalogoDescriptor<T>,
  contexto: ContextoDeCatalogo,
): Promise<void> => {
  await catalogosPersistidos.invalidar(catalogo, contexto)
  await queryClient.invalidateQueries({
    queryKey: criarQueryKeyCatalogo(catalogo.nome, contexto),
  })
}

export const aquecerCatalogo = async <T>(
  queryClient: QueryClient,
  catalogosPersistidos: CatalogosPersistidos,
  catalogo: CatalogoDescriptor<T>,
  contexto: ContextoDeCatalogo,
  consultar: (sinal: AbortSignal) => Promise<readonly T[]>,
  aindaNecessario?: () => boolean,
): Promise<void> => {
  await carregarCatalogo(queryClient, catalogosPersistidos, catalogo, contexto, consultar, aindaNecessario)
    .catch(() => undefined)
}

export const usarCatalogoQuery = <T>(
  configuracao: ConfiguracaoCatalogoQuery<T>,
): EstadoCatalogoQuery<T> => {
  const queryClient = useQueryClient()
  const catalogosPersistidos = inject(CHAVE_CATALOGOS_PERSISTIDOS)
  if (!catalogosPersistidos) {
    throw new Error('Persistencia dos catalogos nao foi instalada')
  }

  const hidratando = ref(true)
  let geracaoHidratacao = 0
  const habilitado = computed(() => configuracao.habilitado?.value ?? true)

  watch([configuracao.contexto, habilitado], async ([contexto, estaHabilitado]) => {
    const geracaoAtual = ++geracaoHidratacao
    hidratando.value = true

    if (contexto && estaHabilitado) {
      await hidratarCatalogoPersistido(
        queryClient,
        catalogosPersistidos,
        configuracao.catalogo,
        contexto,
      )
    }

    if (geracaoAtual === geracaoHidratacao) hidratando.value = false
  }, { immediate: true })

  const queryKey = computed(() => configuracao.contexto.value
    ? criarQueryKeyCatalogo(
      configuracao.catalogo.nome,
      configuracao.contexto.value,
    )
    : ['catalogos', configuracao.catalogo.nome, 'sem-contexto'] as const)

  const query = useQuery({
    queryKey,
    structuralSharing: compartilharSnapshotCatalogo,
    enabled: computed(() => (
      configuracao.contexto.value !== null
      && habilitado.value
      && !hidratando.value
    )),
    staleTime: DURACAO_CATALOGO_FRESCO_MS,
    queryFn: async ({ signal }) => {
      const contexto = configuracao.contexto.value
      if (!contexto) return []

      return criarOpcoesConsultaCatalogo(
        catalogosPersistidos, configuracao.catalogo, contexto, configuracao.consultar,
      ).queryFn({ signal })
    },
  })

  const atualizar = async (): Promise<void> => {
    const contexto = configuracao.contexto.value
    if (!contexto || !habilitado.value) return

    await invalidarCatalogo(
      queryClient,
      catalogosPersistidos,
      configuracao.catalogo,
      contexto,
    )
  }

  return {
    itens: computed(() => {
      const erro = query.error.value
      return erro instanceof FalhaHttp && (erro.status === 401 || erro.status === 403)
        ? [] : query.data.value ?? []
    }),
    erro: computed(() => query.error.value),
    carregando: computed(() => habilitado.value && (
      hidratando.value || query.isLoading.value
    )),
    atualizando: computed(() => query.isFetching.value),
    atualizar,
  }
}
